import { useMutation, useQueryClient } from '@tanstack/react-query';

import type { UpdateEmailSettingsPayload } from './types';

import { apiResourceSchema } from '../common/schemas';
import { QueryKeys } from '../keys';
import { request } from '../request';
import { userSchema } from './schemas';
import { userQueryOptions } from './useUser';

export const useUpdateEmailSettings = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (body: UpdateEmailSettingsPayload) => {
      const response = await request('/user', { method: 'PATCH', body });

      const { data: user } = apiResourceSchema(userSchema).parse(response);

      return user;
    },
    // Optimistically update the user
    onMutate: async (variables) => {
      await queryClient.cancelQueries(userQueryOptions);

      const previousUser = queryClient.getQueryData(userQueryOptions.queryKey);

      queryClient.setQueryData(userQueryOptions.queryKey, (user) => {
        if (!user) return;

        return { ...user, emailSettings: variables.emailSettings };
      });

      return { previousUser };
    },
    // Use the context to roll back if the mutation fails
    onError: (error, variables, context) => {
      queryClient.setQueryData(userQueryOptions.queryKey, () => context?.previousUser);
    },
    // Always refetch after error or success
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: QueryKeys.user });
    },
  });
};
